import React, {ChangeEvent} from 'react';

// Bootstrap
import {isMobile} from 'react-device-detect';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Image from 'react-bootstrap/Image';

// Third libs
import QRCode from 'react-qr-code';
import queryString from 'query-string';
import Cookies from 'universal-cookie';
import {Translation} from 'react-i18next';

// Components
import Translator from '../components/i18n/Translator';

// Styles
import './styles.scss';

// Assets
import playStore from '../assets/img/play-store.png';
import topGradient from '../assets/img/top-gradient.svg';
import bottomGradient from '../assets/img/bottom-gradient.svg';
import multiplyLogo from '../assets/img/multiply-logo.svg';
import multiplyDesk from '../assets/img/multiply-desk.svg';
import appStore from '../assets/img/app-store.png';

// eslint-disable-next-line no-shadow
enum OperatingSystem {
  Android,
  iOS,
}

const TIME_BEFORE_STORE_REDIRECT = 500;

class Home extends React.Component<unknown, {input: string}> {
  // Vars to app urls and voucher code
  storeUrl: string | undefined = '';
  deepLinkUrl: string | undefined = '';
  qrCodeUrl: string | undefined = '';
  voucherCode: string | undefined = '';
  userEmail: string | undefined = '';
  cookies = new Cookies();
  isEmailValid = false;

  constructor(props: any) {
    super(props); // eslint-disable-line @typescript-eslint/no-unsafe-argument
    this.state = {input: ''};

    // Parse and get voucher from querystring - logs an error if failed
    this.voucherCode = this.getVoucher();
    if (!this.voucherCode) {
      console.warn('The app is initialized without an voucher in querystring.');
    }

    // Parse and get the user email
    this.userEmail = this.getEmail();

    // Build QRCode url composed of (base url app + code from querystring + user email)
    this.qrCodeUrl = `${process.env.REACT_APP_URL as string}?voucher=${this.voucherCode}&email=${this.userEmail}`;

    // Check if the email is valid
    if (this.userEmail) {
      this.isEmailValid = this.isValidEmail(this.userEmail);
    }

    this.handleInputChange = this.handleInputChange.bind(this);
    this.preRegisterEmail = this.preRegisterEmail.bind(this);
  }

  componentDidMount(): void {

    // Send the email to pre-register if valid
    if (this.userEmail && this.isEmailValid && this.voucherCode) {
      this.preRegisterEmail();
    }

    // On page load, checks if under a mobile browser and already has an email stored and redirect user async if true
    if (isMobile && this.userEmail) {
      this.buildStoreAndDeepLink();
      this.redirectUserToApp();
    }
  }

  /**
   * Parse and get voucher code from queryString in pattern: '?voucher=ABC123'
   * @returns: The code as string or undefined if not found
   */
  getVoucher(): string | undefined {

    // @ts-ignore
    // eslint-disable-next-line react/prop-types
    const search = this.props.location.search; // Get the search string

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const query = queryString.parse(search);

    // If voucher is valid, then return it's value
    const voucher = query.voucher;
    if (voucher && voucher.length > 0) {
      return voucher as string;
    }

    return undefined;
  }

  /**
   * Parse and get user email from queryString in pattern: '?email=email@ccc.xyz'
   * Also check if a cookie with the email is available
   * @returns: The email as string or undefined if nothing was found
   */
  getEmail(): string | undefined {

    let email = this.cookies.get('email') as string | undefined;

    // @ts-ignore
    // eslint-disable-next-line react/prop-types
    const search = this.props.location.search; // Get the search string

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const query = queryString.parse(search);

    // If voucher an email string is found, use it instead of the cookie
    if (query.email && query.email.length > 0) {
      email = query.email as string | undefined;
      return email;
    }

    return email;
  }

  /**
   * Gets the user mobile operating system. Try to check it by userAgent and another properties
   * Note: This method can fail - in this case, the fallback will be Android
   * @returns: User OperatingSystem (Android or iOS)
   */
  getMobileOperatingSystem(): OperatingSystem {
    // @ts-ignore
    const userAgent = navigator.userAgent || navigator.vendor || window.opera as string;

    // Try to sniff the OS system using the userAgent
    if (/android/i.test(userAgent)) {
      return OperatingSystem.Android;
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return OperatingSystem.iOS;
    }

    // Uses Android as fallback
    return OperatingSystem.Android;
  }

  /**
   * Prepares and build the store url and deep link based on user OS
   */
  buildStoreAndDeepLink(): void {
    if (isMobile) {
      // Get the user mobile OS (from browser sniffing)
      const operatingSystem = this.getMobileOperatingSystem();
      if (operatingSystem === OperatingSystem.Android) {
        // Build URLs (store and deeplink for Android)
        this.storeUrl = process.env.REACT_APP_ANDROID_STORE_LINK as string;
        this.deepLinkUrl = `${process.env.REACT_APP_ANDROID_APP_LINK}?joinapp=true`;
      }

    } else {
      console.error('Error: Trying to build a store and deep link from an non mobile environment.');
    }
  }

  /**
   * We can not from a browser, check if an app is installed or not.
   * So we redirect the phone to app, and if nothing happens redirect the phone to the store url (to download it)
   */
  redirectUserToApp(): void {
    console.log('Redirecting user to Multiply app');

    // Get the user mobile OS (from browser sniffing)
    const operatingSystem = this.getMobileOperatingSystem();

    // Delayed redirection (if deepLink has no effect, user will be redirected to the store)
    if (operatingSystem === OperatingSystem.Android) {
      setTimeout(() => {
        window.location.href = this.storeUrl as string;
      }, TIME_BEFORE_STORE_REDIRECT);

      // Send user to app first - if it fail, the store redirection will be triggered later
      window.location.href = this.deepLinkUrl as string;
    } else if (operatingSystem === OperatingSystem.iOS) {
      // Safari doesn't support URI_SCHEMES redirect, so we use the universal links that should be set for the next page
      window.location.href = process.env.REACT_APP_IOS_APP_PAGE as string;
    } else {
      console.error('Unknown operating system', operatingSystem);
    }
  }

  /**
   * Saves the email input state for further use
   */
  handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
    this.setState({ input: event.target.value });
    this.isEmailValid = this.isValidEmail(event.target.value);
  }

  /**
   * Checks if a string follows an email regex
   */
  isValidEmail(email: string): boolean {
    return /\S+@\S+\.\S+/.test(email);
  }

  /**
   * Sends the browser saved email and voucher to the preregister endpoint
   */
  preRegisterEmail(): void {
    if (this.state.input) {
      this.userEmail = this.state.input;
    }

    // Saves the email in the cookie browser
    this.cookies.set('email', this.userEmail, {maxAge: 10000000});

    // Updates the QR code
    this.qrCodeUrl = `${process.env.REACT_APP_URL as string}?voucher=${this.voucherCode}&email=${this.userEmail}`;

    // If a voucher are missing, abort the operation but update the state
    if (!this.userEmail || !this.voucherCode) {
      this.forceUpdate();
      this.redirectUserToApp();
      return;
    }

    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: this.userEmail, voucher: this.voucherCode })
    };

    // Send the email to the backend preregister endpoint. In other to avoid slowdown we don't validate the return
    const url = `${process.env.REACT_APP_MULTIPLY_SERVICE_URL as string}/pre-register-email`;
    fetch(url, requestOptions).then(() => {}).finally(() => {

      // If mobile, redirects to the app / app store
      if (isMobile) {
        this.redirectUserToApp();
      }
      this.forceUpdate();
    });
  }


  render() {
    let content;

    // Render page content only in desktop browsers - if mobile we check if there's an email inputted
    if (!isMobile || (!this.userEmail)) {
      content = (
        <Container className="content" fluid>
          <Image src={topGradient} className="gradient top-left large"/>
          <Image src={bottomGradient} className="gradient bottom-right large"/>

          <Image src={topGradient} className="gradient top-left small"/>
          <Image src={bottomGradient} className="gradient bottom-right small"/>

          <div className="main-content">

            <Row className="justify-content-center mb-2">
              <Col xs="6" sm="4" md="3" lg="2">
                <Image className="multiply-logo" fluid src={multiplyLogo}/>
              </Col>
            </Row>

            <Row className="justify-content-center message">
              <Col className="multiply-desk" xs="12" sm="12" md="6" lg="4">
                <Image fluid src={multiplyDesk}/>
              </Col>

              <Col xs="12" sm="12" md="6" lg="4">
                <div className="title">
                  <Translator path="home.messageTitle"/>
                </div>
                <div className="main-message">
                  <Translator path="home.message"/>
                </div>
                { !this.userEmail ? (
                  <Row className="justify-content-center">
                    <Col className="email-message">
                      <Translator path="home.emailInput"/>
                    </Col>
                    <Row className="justify-content-left input-padding">
                      <Col xs="12" sm="12" md="6" lg="8" className="p-md-1 mt-2">
                        <Translation>
                          {
                            trn => <input placeholder={trn('home.emailPlaceholder')} className="email-input"
                              onChange={this.handleInputChange} />
                          }
                        </Translation>
                      </Col>
                      <Col xs="12" sm="12" md="4" lg="4" className="p-md-1 mt-2 home-submit">
                        <button disabled={!this.isEmailValid} onClick={this.preRegisterEmail} className="email-submit-btn">
                          <Translator path="home.submitEmail"/>
                        </button>
                      </Col>
                    </Row>
                  </Row>
                ) : (
                  <Row className="qr-code">
                    <Col className="download-title" md="12">
                      <Translator path="home.downloadAppTitle"/>
                    </Col>
                    <Col xs="auto" sm="auto" className="text-right mb-2 my-auto">
                      <Row>
                        <Col>
                          <Image fluid src={playStore} alt="Google play" />
                        </Col>
                      </Row>
                      <Row className="mt-2">
                        <Col>
                          <Image fluid src={appStore} alt="App store" />
                        </Col>
                      </Row>
                    </Col>
                    <Col xs="auto" sm="auto" md="auto" className="text-center">
                      <QRCode size={150} value={this.qrCodeUrl as string} />
                    </Col>
                  </Row>
                ) }
              </Col>
            </Row>
          </div>
        </Container>
      );
    }

    return content;
  }
}

export default Home;
